// libs
import { History } from 'history'
import { flowRight } from 'lodash'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'

// interfaces & constants
import { MAP_TOKEN } from 'src/constants/map'
import {
  IBoundingBox,
  ILocationShape,
  IPosition
} from 'src/interfaces/location'
import { IMapGeoJsonLayer, IMapShape } from 'src/interfaces/map'
import { IMarker } from 'src/interfaces/marker'

// components
import MapComponent from 'src/components/map/map'
import withLoadingSpinner from 'src/high_order_components/with_loading_spinner'

// helpers
import { withRouterProps } from 'src/high_order_components/connect_route_props'
import { getShape, isLockedPath } from 'src/utils/map'

export interface IMapSettings {
  showShape?: boolean;
  inverse?: boolean;
  interactive?: boolean;
}

interface IOwnProps {
  settings?: IMapSettings;
  markers: IMarker[];
  locationShape: ILocationShape;
  showLocationShapeControls?: boolean;
  geoJsonLayers?: IMapGeoJsonLayer[];
}

interface IDispatchToProps {
  setBoundingBox: (boundingBox: IBoundingBox) => void;
}

interface IMapRouteToProps {
  history: History;
  isLocked: boolean;
  locationSlug?: string;
}

type IProps = IOwnProps & IDispatchToProps & IMapRouteToProps;

interface IMapsParams {
  locationSlug: string;
}

const mapRouteToProps = ({
  history,
  location,
  match
}: RouteComponentProps<IMapsParams>): IMapRouteToProps => ({
  history,
  isLocked: isLockedPath(location.pathname),
  locationSlug: match.params.locationSlug
})

const MapContainer: React.FC<IProps> = ({
  settings,
  locationShape,
  locationSlug,
  isLocked,
  markers,
  showLocationShapeControls = true,
  geoJsonLayers
}) => {
  const [shape, setShape] = useState<IMapShape | undefined>(undefined)

  const shouldShowShape = useMemo(() => {
    return locationShape?.slug === locationSlug && settings?.showShape
  }, [locationShape?.slug, locationSlug, settings?.showShape])

  useEffect(() => {
    if (!shouldShowShape || !locationShape) {
      setShape(undefined)
      return
    }

    let cancelled = false

    const updateShape = async () => {
      const newShape = await getShape(locationShape, !!settings?.inverse)
      if (!cancelled) {
        setShape(newShape)
      }
    }

    updateShape()

    return () => {
      cancelled = true
    }
  }, [shouldShowShape, locationShape, settings?.inverse])

  const center: IPosition = useMemo(() => {
    return {
      latitude: locationShape?.center.latitude || 0,
      longitude: locationShape?.center.longitude || 0
    }
  }, [locationShape?.center])

  // The location the map's initial view belongs to, or nothing while the app is still on a
  // different one than the route asks for — following a link to another location shows the stored
  // one first, and opening on its projects would be opening on the wrong place.
  //
  // The id, not the object: the same location loaded again is a new object and not a new place.
  // A portal that carries no slug in its paths (`customLocationSlug`) has none to compare, and
  // there the id alone decides rather than the view never being set at all.
  const locationKey = useMemo(() => {
    if (!locationShape) {
      return undefined
    }
    if (locationSlug === undefined) {
      return locationShape.id
    }
    return locationShape.slug === locationSlug ? locationShape.id : undefined
  }, [locationShape, locationSlug])

  return (
    <MapComponent
      markers={markers}
      mapToken={MAP_TOKEN}
      position={center}
      shape={shape}
      isLocked={isLocked || settings?.interactive === false}
      showLocationShapeControls={showLocationShapeControls}
      geoJsonLayers={geoJsonLayers}
      locationKey={locationKey}
    />
  )
}

export default flowRight(
  withRouterProps(mapRouteToProps),
  withLoadingSpinner
)(MapContainer)
